// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { OrganizationUser } from '@/account/api/types';
import { RegisteredContacts } from '@/account/components/registered-contacts';
import { formatSubscriptionNoticeDate } from '@/account/utilities/format-subscription-expiration-notice';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';

const SUPPORT_EMAIL = 'support@hardfin.com';

interface Props {
  /**
   * If true, the dialog is visible
   */
  isOpen: boolean;

  /**
   * A callback fired when the user acknowledges the notice
   */
  onClose: () => void;

  /**
   * The name of the organization whose subscription expired
   */
  organizationName: string;

  /**
   * The last day of the organization's subscription
   */
  subscriptionEndDate: DateTime;

  /**
   * The first day the organization loses access, when one has been set
   */
  accessEndDate: DateTime | null;

  /**
   * The member registered as the contract contact, when one has been recorded
   */
  contractContact: OrganizationUser | null;

  /**
   * The member registered as the billing contact, when one has been recorded
   */
  billingContact: OrganizationUser | null;
}

interface FactRowProps {
  /**
   * What the date means, such as "Subscription expired"
   */
  label: string;

  /**
   * The calendar date the fact refers to
   */
  date: DateTime;

  /**
   * The start of today, used to describe the date relative to now
   */
  today: DateTime;
}

/**
 * Describes a calendar date in whole days from today, such as "3 days ago" or
 * "in 12 days", so the distance reads the same across a month boundary.
 */
function formatDaysFromToday(date: DateTime, today: DateTime): string | null {
  const day = date.startOf('day');
  if (day.hasSame(today, 'day')) {
    return 'today';
  }
  return day.toRelative({ base: today, unit: 'days' });
}

/**
 * One labeled date in the facts panel: the label and how far away the date is
 * on the left, the date itself right-aligned so the two rows read as a column.
 */
function FactRow(props: FactRowProps) {
  const relative = formatDaysFromToday(props.date, props.today);

  return (
    <>
      <Typography variant="body2">
        {props.label}
        {relative !== null && ' '}
        {relative !== null && (
          <Typography
            component="span"
            variant="body2"
            color="text.secondary"
            sx={{ ml: 1 }}
          >
            {relative}
          </Typography>
        )}
      </Typography>
      <Typography
        variant="body2"
        fontWeight="medium"
        textAlign="right"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {props.date.toLocaleString(DateTime.DATE_FULL)}
      </Typography>
    </>
  );
}

/**
 * A modal that restates the subscription expiration notice when a tab first opens
 * the app. It closes only through its acknowledgement button, so a backdrop
 * click or the escape key leaves the notice on screen.
 */
export function SubscriptionExpirationDialog(props: Props) {
  const today = DateTime.now().startOf('day');

  return (
    <Dialog open={props.isOpen} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        Your organization&apos;s subscription has expired
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography sx={{ textWrap: 'pretty' }}>
            The {props.organizationName} subscription expired on{' '}
            {formatSubscriptionNoticeDate(props.subscriptionEndDate)} and has
            not been renewed.
            {props.accessEndDate !== null &&
              ` Your organization has a grace period until ${formatSubscriptionNoticeDate(props.accessEndDate)}.`}
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="auto 1fr"
            columnGap={3}
            rowGap={1}
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <FactRow
              label="Subscription expired"
              date={props.subscriptionEndDate}
              today={today}
            />
            {props.accessEndDate !== null && (
              <FactRow
                label="Grace period ends"
                date={props.accessEndDate}
                today={today}
              />
            )}
          </Box>
          {props.accessEndDate !== null && (
            <Alert severity="warning">
              Starting {formatSubscriptionNoticeDate(props.accessEndDate)}, no
              one at {props.organizationName} will be able to log in to Hardfin.
            </Alert>
          )}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <RegisteredContacts
              contractContact={props.contractContact}
              billingContact={props.billingContact}
              surface="light"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            To renew, contact Hardfin support at{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={props.onClose}>
          I understand
        </Button>
      </DialogActions>
    </Dialog>
  );
}
